import { useDispatch, useSelector } from "react-redux";
import {
  accountActions,
  changeSignedAgreement,
  getAccountState,
  loadTeamMemberData,
  LoginState,
} from "../redux/slices/accountSlice";
import { useCallback, useContext, useMemo } from "react";
import { AccountData, PhoneInfo } from "@markit/common.types";
import { MixpanelContext } from "../context/AnalyticsService";
import { UserCredential } from "firebase/auth";
import {
  Batch,
  getPhoneNumberRef,
  getUserRef,
  getUserSnap,
} from "../utils/FirebaseUtils";
import { generate } from "shortid";
import { useNavigate, useSearchParams } from "react-router-dom";
import { makeEmptyAccount } from "@markit/common.utils";
import { NavigationId } from "../navigation/AppParamList";
import { OnboardingStage } from "../screens/Login/LoginOnboarding";
import { useMultiAccountManager } from "./useMultiAccountManager";
import { DataLoaders } from "../redux/slices/dataSlice";
import { onNavigateRedirectPathWithParams } from "../utils/navigationUtils";

export const useLogin = () => {
  const {
    redirectPath,
    currentRoleTicket,
    followersData,
    loggedIn,
    accountData: myAccount,
  } = useSelector(getAccountState).account;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mixpanel = useContext(MixpanelContext);
  const { addAdditionalAccount } = useMultiAccountManager();

  const defaultSubscriber = useMemo(
    () => searchParams.has("subscriber"),
    [searchParams]
  );

  const isAddingAccount = useMemo(
    () => loggedIn === LoginState.LOGGED_IN,
    [loggedIn]
  );

  // Handles the case of directing to accept an event role
  // TODO (jonathan): noticed logic here is a bit messy. Don't really understand why finishTempCreation has conditional logic
  //                  on whether it's logging in or signing up. Feels like that should get moved to within loginWithCredential
  const finishTempCreation = useCallback(
    async (
      userData: AccountData,
      isLogin: boolean,
      tempPhoneNumber?: string
    ) => {
      if (currentRoleTicket && isLogin) {
        // Take user to confirmation page which will have a button telling them to accept role
        // did it this way since the confirmation page will figure out if this account already has a role or not
        // to avoid getting double roles
        dispatch(DataLoaders.user(userData.uid));
        dispatch(DataLoaders.event(currentRoleTicket.eventId));
        navigate(
          `/e/${currentRoleTicket.eventId}/i/${userData.uid}?ticketId=${currentRoleTicket.id}`,
          {
            state: { userId: userData.uid, eventId: currentRoleTicket.eventId },
          }
        );
        dispatch(accountActions.setCurrentRoleTicket(undefined));
      } else {
        navigate(NavigationId.ONBOARDING, {
          state: {
            defaultStage:
              defaultSubscriber || userData.email
                ? OnboardingStage.PROFILE_SETUP
                : undefined,
            // Carried through so the creator sign up Slack alert can use it
            signUpPhoneNumber: tempPhoneNumber,
          },
        });
      }
    },
    [currentRoleTicket, dispatch, navigate, defaultSubscriber]
  );

  const loginWithCredential = async (
    credential: UserCredential,
    signIn: boolean,
    tempFullName?: string,
    tempPhoneNumber?: string,
    tempEmail?: string // for social sign in methods
  ) => {
    if (credential.user !== null) {
      const uid = credential.user.uid;
      const accountSnap = await getUserSnap(uid);
      const accountData = accountSnap.data();

      if (accountData) {
        // initialize mixpanel user properties if not created already
        mixpanel.identify(accountData.uid);
        mixpanel.people.set_once({
          $email: accountData.email,
          $phone: accountData.phoneNumber,
          $name: accountData.fullName,
          Followers: followersData.length,
          PaidTicketsSold: 0,
          TotalEventRevenue: 0,
          NetEventRevenue: 0,
          SubscriptionState: "INACTIVE",
          SubscriptionMRR: 0,
        });
        // change signed agreement to true if false for existing user
        if (!accountData.signedAgreement) {
          dispatch(changeSignedAgreement(true, uid));
        }
        // if account has been created for user, login
        if (credential.user) {
          mixpanel.identify(uid);
        }
        mixpanel.track("Webapp: User Login");
        mixpanel.people_increment({ Logins: 1 });
        mixpanel.people.set({
          "Last Login": new Date().toISOString(),
        });

        if (isAddingAccount) {
          // If already logged in, switch to new account and add previous logged in account to additional accounts
          await addAdditionalAccount(myAccount, accountData, !signIn);
        }

        dispatch(accountActions.login(accountData));

        // Load team member context after potential account switch state resets.
        if (accountData.teamOwnerUid) {
          await dispatch(loadTeamMemberData(accountData));
        }

        // don't want to redirect folks on rsvp/follow/external link
        if (signIn) {
          if (currentRoleTicket) {
            await finishTempCreation(
              accountData,
              !accountData.haveNotFinishedInitialStartup
            );
          } else if (accountData.haveNotFinishedInitialStartup) {
            // if account has not set own username (finished sign up flow)
            navigate(NavigationId.ONBOARDING, {
              state: { defaultStage: OnboardingStage.PROFILE_SETUP },
            });
            return;
          } else if (
            redirectPath !== "" &&
            (!isAddingAccount ||
              (isAddingAccount &&
                accountData.phoneNumber &&
                !myAccount.inCreatorMode))
          ) {
            // If not adding additional account OR if adding account, new account is phone based, and old account was subscriber mode
            onNavigateRedirectPathWithParams(
              navigate,
              dispatch,
              redirectPath,
              true
            );
          } else if (accountData.inCreatorMode) {
            navigate("/home", { replace: true });
          } else {
            dispatch(DataLoaders.user(accountData.uid));
            navigate(`/u/${accountData.username}`, {
              state: { username: accountData.username },
              replace: true,
            });
          }
          dispatch(accountActions.setRedirectPath(""));
          window.scrollTo(0, 0);
        }
      } else {
        // if an account has not been created yet for the user, create account
        // create all account data if necessary
        const email = (tempEmail || credential.user.email || "").toLowerCase();
        const phoneNumber = credential.user.phoneNumber;
        const formattedPhoneNumber = phoneNumber || "";
        if (email || phoneNumber) {
          const batch = new Batch();

          const data: AccountData = {
            ...makeEmptyAccount(),
            uid: uid,
            fullName: tempFullName ?? "", // defined when account creation when rsvp/follow
            email: email ?? "",
            username: generate().toLowerCase(),
            inCreatorMode: !!email, // true if new account is email based, false if new account is phone based
            phoneNumber: formattedPhoneNumber,
            haveNotFinishedStartup: true,
            haveNotFinishedInitialStartup: true,
            signedAgreement: true,
          };

          const userRef = getUserRef(uid);
          batch.set(userRef, data);

          if (formattedPhoneNumber !== "") {
            const phoneNumberRef = getPhoneNumberRef(formattedPhoneNumber);

            const phoneInfo: PhoneInfo = {
              lastFreeCreator: "",
              phoneNumber: formattedPhoneNumber,
              userId: uid,
              sentCompliance: false,
              sentComplianceFreeCreatorPhone: false,
              optOut: false,
              optOutFreeCreatorPhone: false,
              optOutTimestamp: "",
            };
            batch.set(phoneNumberRef, phoneInfo);
          }

          // set completedCreatorSetupTimestamp to true
          batch.update(userRef, {
            completedCreatorSetupTimestamp: new Date().toISOString(),
          });

          try {
            await batch.commit();
          } catch (e: any) {
            console.log(e.message);
          }

          mixpanel.track("Webapp: User Created New Account", {
            phone_number: phoneNumber,
            email: email,
            full_name: data.fullName,
            sign_up: "web-app",
          });
          // initialize mixpanel user properties
          mixpanel.register(uid);
          mixpanel.identify(data.uid);
          mixpanel.people.set_once({
            $email: data.email,
            $phone: data.phoneNumber,
            $name: data.fullName,
            Following: 0,
            Followers: 0,
            PaidTicketsSold: 0,
            TotalEventRevenue: 0,
            NetEventRevenue: 0,
            SubscriptionState: "INACTIVE",
            SubscriptionMRR: 0,
          });

          if (isAddingAccount) {
            // If already logged in, switch to new account and add previous logged in account to additional accounts
            await addAdditionalAccount(myAccount, data, !signIn);
          }

          // don't want to redirect folks on rsvp/follow/external link
          if (signIn) {
            await finishTempCreation(data, false, tempPhoneNumber);
          }
        } else {
          alert("An error has occurred");
        }
      }
    }

    return false;
  };

  return { loginWithCredential };
};
